import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import DetailPageTabs, {
	RouteTabItem,
	ListToolbarAction,
} from "../../components/Shared/DetailPageTabs";
import { ContractBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { useContract } from "../../networking/contract.hooks";
import { formatRoute } from "../../utils/route.utils";
import ContractInformationTab from "./ContractInformationTab";
import { ContractRemoveConfirmModal } from "./ContractRemoveConfirmModal";
import ContractTermsTab from "./ContractTermsTab";
import ContractOBTab from "./ContractOBTab";

interface ContractParamProps {
	contractId: string;
}

const ContractDetailsPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const { contractId } = useParams<keyof ContractParamProps>();

	const { contract } = useContract({
		contractId,
	});

	if (!contract) return null;

	const CONTRACT_TAB_GROUPS: RouteTabItem[] = [
		{
			route: routes.CONTRACT_INFO,
			label: "CONTRACT",
			content: <ContractInformationTab contract={contract} />,
		},
		{
			route: routes.CONTRACT_TERMS,
			label: "CONTRACT_TERMS",
			content: <ContractTermsTab contract={contract} />,
		},
		{
			route: routes.OB,
			label: "OB",
			content: <ContractOBTab contract={contract} />,
		},
	];

	const navigateToContractDetails = () => {
		return formatRoute(routes.CONTRACT_DETAILS, [contractId]) + routes.CONTRACT_INFO;
	};

	const ContractDetailsBreadcrumbs: BreadcrumbItem[] = [
		...ContractBreadcrumbs,
		{
			title: (contract?.name || "") + (contract.isDeleted ? ` (${t("contract.archived")})` : ""),
			path: navigateToContractDetails(),
		},
	];

	const actions: ListToolbarAction[] = [
		{
			label: "REMOVE",
			danger: true,
			onClick: () => setShowDeleteModal(true),
		},
	];

	return (
		<>
			<Breadcrumbs items={ContractDetailsBreadcrumbs} />
			<Styles className="p-4 px-6">
				<DetailPageTabs
					tabs={CONTRACT_TAB_GROUPS}
					actions={!contract.isDeleted ? actions : undefined}
				/>
				{showDeleteModal && (
					<ContractRemoveConfirmModal
						contractId={contractId!}
						onCancel={() => setShowDeleteModal(false)}
						onSubmit={() => navigate(routes.CONTRACTS)}
					/>
				)}
			</Styles>
		</>
	);
};

export default ContractDetailsPage;

const Styles = styled.div`
	padding-bottom: 12rem;
`;
